<script setup lang="ts">
import {computed, nextTick, ref} from "vue";
import {CustomFile, MyFileUploader, UploadParam} from "@/types";
import {VideoService} from "@/api/api.ts";
import {loadVideo} from "@/utils/common.ts";
import {SuccessFilled} from "@element-plus/icons-vue";
import RecordVideoListDialog from "components/dialog/record/RecordVideoListDialog.vue";
import {ElNotification} from "element-plus";

const recordVideoListDialog = ref<InstanceType<typeof RecordVideoListDialog>>();
// 转码成功
const transformComplete = ref(false);
let player: Aliplayer
let videoId = "";
const videoName = ref<string>("")
const localUrl = ref<string>("")
const upload = ref<HTMLInputElement>()
const fileUploader = ref<MyFileUploader>()
const selectOrUploadSuccess = ref(false)
/**
 * 当前上次视频的进度
 */
const videoProgress = ref<number>(0)

const onSelectVideo = (video_id: string, video_name: string) => {
  videoId = video_id
  videoName.value = video_name
  // 选择完成后隐藏
  recordVideoListDialog.value?.hide()
  selectOrUploadSuccess.value = true
  player?.dispose()
  setTimeout(() => {
    initRemoteVideoPlayer()
  }, 1000)
}

const emit = defineEmits(['onVideoNameChange', 'onVideoIdChange', "onPreviewVideo"])
const createUploader = (
    customFileUploader: MyFileUploader
) => {
  customFileUploader.uploader = new AliyunUpload.Vod({
    timeout: 60000,
    partSize: Math.round(1048576),
    parallel: 5,
    retryCount: 3,
    retryDuration: 2,
    region: import.meta.env.VITE_VOD_REGION,
    userId: "1557089946584947",
    localCheckpoint: false, //此参数是禁用服务端缓存，不影响断点续传
    // 添加文件成功
    addFileSuccess: function (uploadInfo: UploadInfo) {
      console.log("addFileSuccess: " + uploadInfo.file.name);
    },
    // 开始上传
    onUploadstarted: function (uploadInfo: UploadInfo) {
      // 如果是 UploadAuth 上传方式, 需要调用 uploader.setUploadAuthAndAddress 方法
      // 如果是 UploadAuth 上传方式, 需要根据 uploadInfo.videoId是否有值，调用点播的不同接口获取uploadauth和uploadAddress
      // 如果 uploadInfo.videoId 有值，调用刷新视频上传凭证接口，否则调用创建视频上传凭证接口
      // 如果 uploadInfo.videoId 存在, 调用 刷新视频上传凭证接口(https://help.aliyun.com/document_detail/55408.html)
      // 如果 uploadInfo.videoId 不存在,调用 获取视频上传地址和凭证接口(https://help.aliyun.com/document_detail/55407.html)
      if (!uploadInfo.videoId) {
        const uploadParams: UploadParam[] = []
        const uploadParam: UploadParam = {
          file_name: customFileUploader.file.name,
          title: customFileUploader.file.name,
          duration: customFileUploader.file.duration as number,
          width: customFileUploader.file.width as number,
          height: customFileUploader.file.height as number
        }
        uploadParams.push(uploadParam)
        VideoService.gainUploadAuth({
          video_data: JSON.stringify(uploadParams)
        }).then(res => {
          if (res.data.code === 200 && res.data.data && res.data.data.length > 0) {
            customFileUploader.uploader.setUploadAuthAndAddress(
                uploadInfo,
                res.data.data[0].UploadAuth, res.data.data[0].UploadAddress,
                res.data.data[0].VideoId,
            )
          } else {
            // 出问题
          }
        })
      } else {
        if (uploadInfo.videoId) {
          VideoService.refreshUploadAuth({
            video_id: uploadInfo.videoId,
          }).then(res => {
            if (res.data.code === 200 && res.data.data) {
              const uploadAuth = res.data.data.UploadAuth
              const uploadAddress = res.data.data.UploadAddress
              const videoId =  res.data.data.VideoId
              customFileUploader.uploader.setUploadAuthAndAddress(uploadInfo, uploadAuth, uploadAddress, videoId)
            }
          })
        }
      }
    },
    // 文件上传成功
    onUploadSucceed: function (uploadInfo: UploadInfo) {
      console.log(
          "onUploadSucceed: " +
          uploadInfo.file.name +
          ", endpoint:" +
          uploadInfo.endpoint +
          ", bucket:" +
          uploadInfo.bucket +
          ", object:" +
          uploadInfo.object +
          ", video_id:" + uploadInfo.videoId
      );
      videoId = uploadInfo.videoId
      selectOrUploadSuccess.value = true
      emit('onVideoIdChange', uploadInfo.videoId)
    },
    // 文件上传失败
    onUploadFailed: function (uploadInfo: UploadInfo, code: number, message: string) {
      console.log(
          "onUploadFailed: file:" + uploadInfo.file.name + ",code:" + code + ", message:" + message
      );
      customFileUploader.status = 2
    },
    // 取消文件上传
    onUploadCanceled: function (uploadInfo: UploadInfo, code: number, message: string) {
      console.log(
          "Canceled file: " + uploadInfo.file.name + ", code: " + code + ", message:" + message
      );
    },
    // 文件上传进度，单位：字节, 可以在这个函数中拿到上传进度并显示在页面上
    onUploadProgress: function (uploadInfo: UploadInfo, totalSize: number, progress: number) {
      console.log(uploadInfo, totalSize, progress)
      customFileUploader.file.progress = Math.ceil(progress * 100);
      videoProgress.value = Math.ceil(progress * 100)
    },
    // 上传凭证超时
    onUploadTokenExpired: function (uploadInfo: UploadInfo) {
      // 上传大文件超时, 如果是上传方式一即根据 UploadAuth 上传时
      // 需要根据 uploadInfo.videoId 调用刷新视频上传凭证接口(https://help.aliyun.com/document_detail/55408.html)重新获取 UploadAuth
      // 然后调用 resumeUploadWithAuth 方法, 这里是测试接口, 所以我直接获取了 UploadAuth
      if (uploadInfo.videoId) {
        VideoService.refreshUploadAuth({
          video_id: uploadInfo.videoId,
        }).then(res => {
          if (res.data.code === 200 && res.data.data) {
            const uploadAuth = res.data.data.UploadAuth
            const uploadAddress = res.data.data.UploadAddress
            const videoId =  res.data.data.VideoId
            customFileUploader.uploader.setUploadAuthAndAddress(uploadInfo, uploadAuth, uploadAddress, videoId)
          }
        })
      }
    },
    // 全部文件上传结束
    onUploadEnd: function (uploadInfo: UploadInfo) {
      console.log(uploadInfo + "onUploadEnd: uploaded all the files");
      customFileUploader.status = 1
    },
  });
  customFileUploader.uploader.addFile(customFileUploader.file, null,null,null,'{"Vod":{}}');
  customFileUploader.uploader.startUpload();
};

const onFileChange = (e: any) => {
  if (upload.value && upload.value.files && upload.value.files.length > 0) {
    const file = upload.value.files[0] as CustomFile
    localUrl.value = ""
    selectOrUploadSuccess.value = false
    loadVideo(file).then((video) => {
      transformComplete.value = false
      localUrl.value = URL.createObjectURL(file)
      videoName.value = file.name
      nextTick(() => {
        player?.dispose();
        setTimeout(() => {
          initLocalPlayer()
        }, 1000);
      })
      file.duration = Math.ceil(video.duration)
      file.width = video.videoWidth
      file.height = video.videoHeight
      console.log(file.width, file.height, file.duration)
      if (fileUploader.value && fileUploader.value.uploader) {
        fileUploader.value.uploader.stopUpload();
        fileUploader.value = {
          file: file,
          status: 0,
          uploader: null,
        }
      } else {
        fileUploader.value = {
          file: file,
          status: 0,
          uploader: null,
        }
      }
      e.target.value = null
      // 重置videoId
      videoId = ""
      createUploader(fileUploader.value)
      emit('onVideoNameChange', fileUploader.value.file.name)
    }).catch(err => {
      console.warn(err)
    })
  }
}

const selectVideo = () => {
  recordVideoListDialog.value?.show()
}

/**
 * 停止上传
 */
const stopUpload = () => {
  if (fileUploader.value) {
    fileUploader.value.uploader?.stopUpload()
    fileUploader.value.status = 3
    videoProgress.value = 0
    videoName.value = ""
  }
}

const getVideoId = () => {
  return videoId
}

/**
 * 是否正上传
 */
const isUploading = () => {
  if (!fileUploader.value) {
    return false
  }
  console.log(fileUploader.value.status)
  return fileUploader.value.status === 0
}

const clearVideo = () => {
  stopUpload()
  videoId = ""
  videoName.value = ""
  localUrl.value = ""
  transformComplete.value = false
  selectOrUploadSuccess.value = false
  player?.dispose()
}

const initLocalPlayer = () => {
  const playerOption = {
    id: "player-container",
    width: "400px",
    height: "300px",
    autoplay: true,
    source: localUrl.value,
    encryptType: 0,
  }
  player = new Aliplayer(playerOption)
}

const initRemoteVideoPlayer = async () => {
  try {
    const statusRes = await VideoService.getVideoStatus({
      video_id: videoId
    })
    if (statusRes.data.code === 200) {
      if (statusRes.data.data === 1) {
        transformComplete.value = true
        await nextTick()
        // 转码完成，获取播放凭证
        await getAuth()
      } else {
        // 转码中
        transformComplete.value = false
      }
    } else {
      ElNotification.error({
        title: '获取视频转码状态失败',
        message: statusRes.data.msg,
        type: 'error',
      })
    }
  } catch (error) {
    console.warn(error)
  } finally {
  }
}

const getAuth = async () => {
  try {
    const res = await VideoService.getPlayAuth({
      video_id: videoId
    })
    if (res.data.code === 200) {
      await nextTick(() => {
        const option: PlayerOption = {
          id: "player-container",
          width: "400px",
          height: "300px",
          autoplay: true,
          vid: videoId,
          playauth: res.data.data?.PlayAuth,
          cover: res.data.data?.CoverURL,
          encryptType: 1,
        }
        player = new Aliplayer(option)
      })
    } else {
      ElNotification.error({
        title: '获取播放凭证失败',
        message: res.data.msg,
        type: 'error',
      })
    }
  } catch (error) {
    console.warn(error)
  } finally {
  }
}

const uploadBtnText = computed(() => {
  if (fileUploader.value && fileUploader.value.status === 0) {
    return "上传视频"
  }
  if (videoName.value) {
    return "上传视频"
  }
  return "上传视频"
})

const onPreviewVideo = (video_id: string) => {
  emit("onPreviewVideo", video_id)
}

defineExpose({
  stopUpload, clearVideo, getVideoId, isUploading
})
</script>

<template>
  <div class="w-full">
    <div>
      <el-button type="primary" @click="upload?.click()">{{ uploadBtnText }}</el-button>
      <el-button type="warning" :disabled="fileUploader && fileUploader.status === 0" @click="selectVideo">选择视频</el-button>
      <input accept=".mp4" ref="upload" type="file" id="upload" class="hidden" @change="onFileChange" />
    </div>
    <!-- 播放器区域 -->
    <div id="player-container" ref="playerCon" v-if="localUrl || transformComplete" class="mt-1rem">
    </div>
    <!-- 文件信息区域 -->
    <div class="flex items-center mt-1rem" v-if="videoName">
      <div class="text-16px">{{ `当前已选新视频：` }}</div>
      <div class="text-orange mr-1rem">{{ videoName }}</div>
      <div class="mr-.5rem" v-if="selectOrUploadSuccess">
        <el-icon><SuccessFilled /></el-icon>
      </div>
      <el-button type="danger" @click="clearVideo">清除视频</el-button>
    </div>
    <!-- 上传进度条区域 -->
    <div class="mt-1rem flex items-center" v-if="fileUploader && fileUploader.status === 0">
      <el-progress :percentage="videoProgress" style="width: 20rem" />
    </div>
  </div>
  <record-video-list-dialog ref="recordVideoListDialog" @on-select-video="onSelectVideo" @on-preview-video="onPreviewVideo"/>
</template>

<style scoped>
</style>
